import {
  API_RESOURCE_TYPES,
  resolveResourceType,
  type ApiResourceType
} from '@/shared/resourceModuleParity'

import type { GuidanceAction, GuidanceRewardBasis } from './types'

/** Só o que pontua importa aqui — o resto do snapshot não é lido. */
export interface RankingGuidanceSnapshot {
  missions: Array<{ point_reward?: number }>
  quizzes: Array<{ point_reward?: number }>
  documents: Array<{ point_reward?: number }>
}

interface ScoringSource {
  resourceType: ApiResourceType
  values: number[]
  per: GuidanceRewardBasis
}

const highest = (values: number[]): number =>
  values.reduce((max, value) => (value > max ? value : max), 0)

/**
 * "conclua" → "Conclua", "leia" → "Leia", "participe de" → "Participe de".
 *
 * O verbo vem do registry por módulo; capitalizar aqui evita uma segunda
 * tabela de verbos, que divergiria da de `resourceCopy` sem nada acusar.
 */
const capitalize = (value: string): string =>
  value.charAt(0).toUpperCase() + value.slice(1)

const rewardsOf = (items: Array<{ point_reward?: number }>): number[] =>
  items.map(item => item.point_reward ?? 0)

const RESOURCE_ORDER = new Map(
  API_RESOURCE_TYPES.map((type, index) => [type, index])
)

/**
 * Como pontuar naquele evento, montado do que o produtor de fato configurou.
 *
 * É a versão derivada do `HowToScoreWidget`, que listava quatro ações fixas no
 * código e por isso sugeria quizzes a evento sem quiz.
 *
 * **A unidade é o módulo, não o tipo de recurso.** Jornada, etapa, nível e
 * desafio carregam `point_reward` e todos levam a `/jornadas`; uma ação por
 * tipo daria quatro linhas quase idênticas.
 *
 * `journeyTotals` são os `_totalPointReward` de `useJourneysListView()` — o
 * total por jornada é derivado, não campo do snapshot (`total_point_reward` é
 * de `Level`). Entra por parâmetro para o builder seguir puro.
 *
 * Fora daqui de propósito: `posts` e `programmings`, que o store tipa como
 * `unknown[]` por decisão registrada, e `Partner`, que não declara
 * `point_reward` no aplicativo.
 */
export const buildRankingGuidance = (
  snapshot: RankingGuidanceSnapshot,
  journeyTotals: number[]
): GuidanceAction[] => {
  const sources: ScoringSource[] = [
    { resourceType: 'Journey', values: journeyTotals, per: 'item' },
    { resourceType: 'Mission', values: rewardsOf(snapshot.missions), per: 'item' },
    {
      resourceType: 'Quiz',
      values: rewardsOf(snapshot.quizzes),
      per: 'correct-answer'
    },
    {
      resourceType: 'Document',
      values: rewardsOf(snapshot.documents),
      per: 'item'
    }
  ]

  return sources
    .map(source => ({ source, points: highest(source.values) }))
    .filter(({ points }) => points > 0)
    .sort((a, b) => {
      if (b.points !== a.points) return b.points - a.points

      // Desempate por `API_RESOURCE_TYPES`, que é constante. `activeModuleTypes`
      // seria instável: é um `Set` derivado de `pages`, e fica vazio justamente
      // no estado em que o filtro de módulo não roda.
      return (
        (RESOURCE_ORDER.get(a.source.resourceType) ?? 0) -
        (RESOURCE_ORDER.get(b.source.resourceType) ?? 0)
      )
    })
    .map(({ source, points }) => {
      const resolved = resolveResourceType(source.resourceType)

      return {
        key: `ranking-${source.resourceType.toLowerCase()}`,
        resourceType: source.resourceType,
        title: `${capitalize(resolved.completeVerb ?? 'conclua')} ${resolved.label.plural.toLowerCase()}`,
        instruction: `Acesse ${resolved.sectionPrep} para pontuar.`,
        reward: { points, per: source.per },
        to: resolved.route
      }
    })
}
